import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import "../../../UserDashboard/components/UserDashboardHome/UserDashboardHome.css";
import localeText from "../../../../locale";
import { Toast } from "primereact/toast";
import "../../../UserDashboard/components/StatementTable/StatementTable.css";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { cloudinaryInstance } from "../../../../utilities/Cloudinary/cloudinaryInstance";
import PhysicalStoreService from "../../../../service/PhysicalStoreService";

const physicalStoreService = new PhysicalStoreService();

const ListPhysicalStores = () => {
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [search, setSearch] = useState("");
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);

  const toast = useRef(null);

  useEffect(() => {
    let cancel = false;

    async function fetchData() {
      try {
        setLoading(true);
        const res = await physicalStoreService.getApprovedStores();

        if (cancel) return;

        if (res.messageCode !== 200001) {
          toast.current.show({
            severity: "error",
            summary: localeText("error"),
            detail: localeText("data").find((msg) => msg.code === res.messageCode)?.message,
            life: 6000,
          });
          setLoading(false);
        } else {
          setStores(res.result);
          setFilteredStores(res.result);
          setLoading(false);
        }
      } catch (e) {
        if (e.status === 401 || e.status === 403) {
          localStorage.removeItem("__extraconomy_token");
          localStorage.removeItem("__extraconomy_username");
          window.location.replace("/login");
        }
        setLoading(false);
        console.log(e);
      }
    }

    fetchData();

    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterByName = (name) => {
    if (name) {
      const filtered = stores.filter((s) =>
        s.physicalStoreName?.toLowerCase().includes(name.toLowerCase()),
      );
      setFilteredStores([...filtered]);
    } else {
      setFilteredStores(stores);
    }
  };

  const photoTemplate = (store) => {
    if (!store.proofPhotoId) return <span>—</span>;
    return (
      <AdvancedImage
        cldImg={cloudinaryInstance
          .image(store.proofPhotoId)
          .resize(fill().width(80).height(80))
          .format("webp")}
        height={80}
        style={{ border: "1px solid #E0E0E0", borderRadius: 6 }}
      />
    );
  };

  const addressTemplate = (store) => {
    const parts = [
      store.physicalAddress,
      store.physicalNumber,
      store.physicalDistrict,
      store.physicalCity,
      store.physicalState,
      store.physicalPostalCode,
      store.physicalCountry,
    ].filter(Boolean);
    return <span>{parts.join(", ")}</span>;
  };

  const actionsTemplate = (store) => (
    <Button
      icon="pi pi-eye"
      className="p-button-sm bg-blue-600 border-blue-600 hover:bg-blue-700 hover:border-blue-700"
      tooltip={localeText("viewDetails")}
      tooltipOptions={{ position: "top" }}
      onClick={() => {
        setSelectedStore(store);
        setDetailsVisible(true);
      }}
    />
  );

  const detailsDialogContent = () => {
    if (!selectedStore) return null;
    const s = selectedStore;

    const rowStyle = { display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" };
    const labelStyle = { fontWeight: 600, minWidth: 180, flexShrink: 0 };

    const DetailRow = ({ label, value }) =>
      value ? (
        <div style={rowStyle}>
          <span style={labelStyle}>{label}:</span>
          <span>{value}</span>
        </div>
      ) : null;

    return (
      <div>
        <h4 className="mb-3 mt-0" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 8 }}>
          {localeText("establishmentPhoto")}
        </h4>
        {s.proofPhotoId && (
          <div className="mb-2">
            <AdvancedImage
              cldImg={cloudinaryInstance
                .image(s.proofPhotoId)
                .resize(fill().width(200).height(200))
                .format("webp")}
              style={{
                border: "1px solid #E0E0E0",
                borderRadius: 6,
                display: "block",
                marginBottom: 8,
              }}
            />
          </div>
        )}
        {s.proofPhotoUrl && (
          <Button
            label={localeText("openPhoto")}
            icon="pi pi-external-link"
            className="p-button-outlined p-button-sm mb-4"
            onClick={() => window.open(s.proofPhotoUrl, "_blank", "noopener,noreferrer")}
          />
        )}

        <h4 className="mb-3" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 8 }}>
          {localeText("physicalStoreName")}
        </h4>
        <DetailRow label={localeText("physicalStoreName")} value={s.physicalStoreName} />
        <DetailRow
          label={localeText("adminBackofficeAddress")}
          value={[
            s.physicalAddress,
            s.physicalNumber,
            s.physicalDistrict,
            s.physicalCity,
            s.physicalState,
            s.physicalPostalCode,
            s.physicalCountry,
          ]
            .filter(Boolean)
            .join(", ")}
        />
        <DetailRow
          label={localeText("submittedAt")}
          value={s.submittedAt ? new Date(s.submittedAt).toLocaleDateString() : undefined}
        />
      </div>
    );
  };

  return (
    <div>
      <Toast ref={toast} />

      <Dialog
        header={localeText("storeDetails")}
        visible={detailsVisible}
        style={{ width: "560px" }}
        draggable={false}
        onHide={() => {
          setDetailsVisible(false);
          setSelectedStore(null);
        }}
      >
        {detailsDialogContent()}
      </Dialog>

      <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center" }}>
        <InputText
          className="w-100"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            filterByName(e.target.value);
          }}
          placeholder={localeText("searchByStoreName")}
        />
      </div>

      <hr />

      <DataTable
        className="mt-4 mb-6 p-datatable-customers"
        value={filteredStores}
        paginator
        responsiveLayout="scroll"
        loading={loading}
        dataKey="id"
        filterDisplay="row"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate={localeText("showingFirstToLast")}
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
      >
        <Column header={localeText("image")} body={photoTemplate} style={{ width: "8%" }} />
        <Column
          field="physicalStoreName"
          header={localeText("physicalStoreName")}
          sortable
          style={{ width: "22%" }}
        />
        <Column
          header={localeText("adminBackofficeAddress")}
          body={addressTemplate}
          style={{ width: "40%" }}
        />
        <Column
          field="store.username"
          header={localeText("storeOwner")}
          sortable
          style={{ width: "20%" }}
        />
        <Column header={localeText("actions")} body={actionsTemplate} style={{ width: "10%" }} />
      </DataTable>
    </div>
  );
};

export default ListPhysicalStores;
